import { useRef, useContext } from "react";
import { Button, Group } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { IconDownload } from "@tabler/icons-react";
import { AppName } from "../../Lib/data";
import { AllFatherContext } from "../../App";
import "./styles.scss";

export default function Certificate() {
  const context = useContext(AllFatherContext);
  const { t } = useTranslation();
  const data = {
    name: "John Doe",
    course: "React Course",
    date: "2021-01-01",
    id: "1234567890",
  };
  const certRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    if (!certRef.current) return;
    const printContents = certRef.current.innerHTML;
    const win = window.open("", "_blank");
    win!.document.write(`
      <html>
        <head>
          <title>Certificate</title>
          <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@700;900&family=Inter:wght@400;700&display=swap" rel="stylesheet">
          <style>
            body { background: #f8f9fa; margin: 0; }
            .certificate-print {
              width: 900px; margin: 40px auto; padding: 48px 56px; background: rgba(255,255,255,0.98); border-radius: 18px; box-shadow: 0 8px 48px rgba(120,80,255,0.10); border: 2.5px solid #a084ee; font-family: 'Montserrat', 'Inter', Arial, sans-serif;
            }
            .cert-title { font-size: 2.8rem; font-weight: 900; letter-spacing: -2px; margin-bottom: 18px; background: linear-gradient(90deg,#845ef7 0%,#a084ee 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
            .cert-name { font-size: 2.2rem; font-weight: 800; margin: 18px 0 8px 0; color: #4b2995; }
            .cert-course { font-size: 1.5rem; font-weight: 700; color: #845ef7; margin-bottom: 18px; }
            .cert-date { font-size: 1.1rem; color: #888; margin-bottom: 32px; }
            .cert-logo { width: 90px; margin-bottom: 18px; }
            .cert-signature { margin-top: 48px; display: flex; justify-content: flex-end; align-items: center; gap: 18px; }
            .cert-signature-line { border-bottom: 2px solid #a084ee; width: 180px; height: 32px; }
            .cert-signature-label { color: #a084ee; font-weight: 700; font-size: 1.1rem; letter-spacing: 1px; }
          </style>
        </head>
        <body>
          <div class="certificate-print">${printContents}</div>
        </body>
      </html>
    `);
    win!.document.close();
    setTimeout(() => win!.print(), 300);
  };

  return (
    <div className="certificate-page">
      <Group justify="end" mb={24}>
        <Button
          leftSection={<IconDownload size={18} />}
          color="violet"
          radius="xl"
          size="md"
          onClick={handlePrint}
        >
          {t("certificate.downloadPdf")}
        </Button>
      </Group>
      <div className="certificate-container" ref={certRef}>
        <div className="certificate-bg-graphics">
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 1100 700"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ position: "absolute", inset: 0 }}
          >
            <defs>
              <linearGradient
                id="wave1"
                x1="0"
                y1="0"
                x2="1100"
                y2="700"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#e0c3fc" stopOpacity="0.32" />
                <stop offset="1" stopColor="#8ec5fc" stopOpacity="0.18" />
              </linearGradient>
              <linearGradient
                id="wave2"
                x1="0"
                y1="700"
                x2="1100"
                y2="0"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#a084ee" stopOpacity="0.13" />
                <stop offset="1" stopColor="#fbc2eb" stopOpacity="0.10" />
              </linearGradient>
            </defs>
            <ellipse cx="900" cy="-80" rx="320" ry="180" fill="url(#wave1)" />
            <ellipse cx="200" cy="780" rx="320" ry="180" fill="url(#wave2)" />
            <ellipse cx="1050" cy="650" rx="90" ry="60" fill="#a084ee" fillOpacity="0.08" />
            <ellipse cx="50" cy="50" rx="90" ry="60" fill="#845ef7" fillOpacity="0.07" />
          </svg>
        </div>
        <div className="certificate-content">
          {/* Logo */}
          <div className="certificate-header">
            <img src={context?.assets?.logos?.legacy} alt="logo" className="cert-logo" />
          </div>
          <div
            className="cert-title"
            style={{
              fontSize: "1.2rem",
              letterSpacing: 2,
              color: "#888",
              fontWeight: 700,
              marginBottom: 8,
            }}
          >
            {t("certificate.professionalCertification")}
          </div>
          <div
            className="cert-course"
            style={{
              fontSize: "2.8rem",
              fontWeight: 900,
              margin: "0 0 18px 0",
              color: "#18181b",
              letterSpacing: "-2px",
              textAlign: "center",
            }}
          >
            {data.course}
          </div>
          <div
            className="cert-title"
            style={{
              fontSize: "1.1rem",
              color: "#888",
              fontWeight: 600,
              marginBottom: 18,
              letterSpacing: 1,
            }}
          >
            {t("certificate.issuedTo")}
          </div>
          <div
            className="cert-name"
            style={{
              fontSize: "2rem",
              fontWeight: 800,
              color: "#18181b",
              marginBottom: 12,
              textAlign: "center",
            }}
          >
            {data.name}
          </div>
          <div
            className="cert-desc"
            style={{
              maxWidth: 600,
              margin: "0 auto 32px auto",
              color: "#888",
              fontSize: 18,
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            The bearer of this professional certificate has demonstrated a fundamental level of
            mastery and passed the core competencies for this course.
          </div>
          <div
            className="cert-footer-row"
            style={{
              display: "flex",
              width: "100%",
              alignItems: "flex-end",
              justifyContent: "space-between",
              marginTop: 40,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 8,
              }}
            >
              <div className="cert-signature" style={{ margin: 0, marginBottom: 8 }}>
                <img
                  src="https://svgshare.com/i/12uA.svg"
                  alt="signature"
                  style={{ height: 36, marginRight: 8 }}
                />
                <span style={{ color: "#18181b", fontWeight: 600, fontSize: 15 }}>
                  {t("certificate.director")}
                </span>
              </div>
              <span style={{ color: "#888", fontSize: 14, fontWeight: 500 }}>{AppName}</span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 6,
              }}
            >
              <span style={{ color: "#888", fontSize: 14 }}>
                {t("certificate.issued")}: {data.date}
              </span>
              <span style={{ color: "#888", fontSize: 14 }}>
                {t("certificate.id")}: {data.id}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
